// Primitivas de layout del wizard — port 1:1 de `mockups/_shared/layout.css` (.wizard-card*).
// "El mock manda": las medidas de acá salen del CSS del mockup, no de una interpretación libre.
//   .wizard-card      → max-w-[680px] (720px ≥900px), radius 12, shadow suave, overflow hidden
//   .wizard-card-head → padding 20/24/16, borde inferior (apretado en celular)
//   .wizard-card-eyebrow → 10px uppercase tracking .08em, weight 600
//   .wizard-card-title/sub → 20px/600 · 13px muted
//   .wizard-card-body → padding 24
//   .wizard-card-foot → padding 16/24, borde superior, fondo muted/30, space-between
//                       (en celular se apila, con la acción primaria arriba)
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export function WizardCard({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <div
            className={cn(
                'w-full max-w-[680px] overflow-hidden rounded-xl border border-border bg-card shadow-[0_4px_12px_rgba(0,0,0,0.04)] lg:max-w-[720px]',
                className,
            )}
        >
            {children}
        </div>
    );
}

export function WizardCardHead({
    eyebrow,
    title,
    sub,
    right,
    help,
}: {
    eyebrow?: string;
    title: string;
    sub?: ReactNode;
    /** Esquina superior derecha del head. */
    right?: ReactNode;
    /** Botón «?» junto al título, fuera del encabezado para no cambiar su nombre accesible. */
    help?: ReactNode;
}) {
    const heading = (
        <h1 className="text-[17px] font-semibold leading-[1.25] tracking-[-0.01em] sm:text-[20px]">
            {title}
        </h1>
    );
    return (
        <div className="border-b border-border px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
            <div className="flex flex-col items-start gap-2 sm:flex-row sm:justify-between sm:gap-3">
                <div className="min-w-0 flex-1">
                    {eyebrow ? (
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {eyebrow}
                        </div>
                    ) : null}
                    {help ? (
                        <div className="flex items-center gap-1.5">
                            {heading}
                            {help}
                        </div>
                    ) : (
                        heading
                    )}
                    {sub ? (
                        <p className="mt-1.5 text-[13px] leading-[1.5] text-muted-foreground">
                            {sub}
                        </p>
                    ) : null}
                </div>
                {right ? (
                    <div className="order-first flex-none sm:order-none sm:pt-1">{right}</div>
                ) : null}
            </div>
        </div>
    );
}

export function WizardCardBody({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return <div className={cn('p-4 sm:p-6', className)}>{children}</div>;
}

export function WizardCardFoot({ children }: { children: ReactNode }) {
    return (
        <div className="flex flex-col-reverse items-stretch gap-2 border-t border-border bg-muted/30 px-4 py-3 max-sm:[&>*]:justify-center sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-6 sm:py-4">
            {children}
        </div>
    );
}

/** Sección dentro del body, separada por línea (el mock divide bloques con border-top). */
export function WizardSection({ label, children }: { label?: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-2">
            {label ? (
                <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {label}
                </div>
            ) : null}
            {children}
        </div>
    );
}
